import { FormikProps } from "formik";
import { lowerCase, upperFirst } from "lodash";
import { useTranslation } from "react-i18next";
import { FormValues } from "components/ProfileForm/support";
import {
  Button,
  Input,
  InputGroup,
  Select,
  Spinner,
  Tooltip,
  typography,
  type InputProps,
  type SelectProps,
} from "@dsaui/components";
import { RadioGroup } from "@dsaui/components/forms";
import { cn } from "@dsaui/utils";

const profileAutocomplete: Partial<
  Record<keyof FormValues, InputProps["autoComplete"]>
> = {
  email: "email",
  firstName: "given-name",
  chosenFirstName: "given-name",
  middleName: "additional-name",
  lastName: "family-name",
  chosenLastName: "family-name",
  phone: "tel",
  primaryLine1: "address-line1",
  primaryLine2: "address-line2",
  primaryCity: "address-level2",
  primaryState: "address-level1",
  primaryZip: "postal-code",
  primaryCountry: "country-name",
  mailingLine1: "address-line1",
  mailingLine2: "address-line2",
  mailingCity: "address-level2",
  mailingState: "address-level1",
  mailingZip: "postal-code",
  mailingCountry: "country-name",
};

interface ProfileFormInputProps extends InputProps {
  name: keyof FormValues;
  fp: FormikProps<FormValues>;
}

interface ProfileFormSelectProps extends SelectProps {
  name: keyof FormValues;
  fp: FormikProps<FormValues>;
}

export const ProfileFormInput = ({
  name,
  fp: { values, errors, handleChange, handleBlur, touched },
  ...rest
}: ProfileFormInputProps) => {
  // Necessary to avoid uncontrolled input warning
  const currentValue = values[name] ?? "";
  const { t } = useTranslation();

  return (
    <Input
      id={name}
      name={name}
      autoComplete={profileAutocomplete[name]}
      placeholder={t(`forms.placeholders.${name}`)}
      label={t(`forms.labels.${name}`)}
      onChange={handleChange}
      onBlur={handleBlur}
      value={currentValue as string}
      errorMessage={
        touched[name] && typeof errors[name] !== "undefined"
          ? t(errors[name] as string, { value: upperFirst(lowerCase(name)) })
          : ""
      }
      {...rest}
    />
  );
};

export const ProfileFormSelect = ({
  name,
  disabled,
  required,
  fp: { values, errors, handleChange, touched },
  children,
  ...rest
}: ProfileFormSelectProps) => {
  // Necessary to avoid uncontrolled input warning
  const currentValue = values[name] ?? "";
  const { t } = useTranslation();

  return (
    <div className="mb-4">
      <Select
        id={name}
        name={name}
        autoComplete={profileAutocomplete[name]}
        label={t(`forms.labels.${name}`)}
        onChange={handleChange}
        value={currentValue as string}
        disabled={disabled}
        errorMessage={
          touched[name] && typeof errors[name] !== "undefined"
            ? t(errors[name] as string, { value: upperFirst(lowerCase(name)) })
            : ""
        }
        required={required}
        {...rest}
      >
        <option value="" disabled>
          {t(`forms.placeholders.${name}`)}
        </option>
        {children}
      </Select>
    </div>
  );
};

interface ProfileFormRadioGroupProps {
  name:
    | "dlMailingPreference"
    | "mailingPreference"
    | "unionLeadership"
    | "gender"
    | "lgbtq"
    | "unionMember";
  options: string[];
  fp: FormikProps<FormValues>;
  tooltip?: boolean;
}

export const ProfileFormRadioGroup = ({
  name,
  options,
  fp: { values, handleChange },
  tooltip = false,
}: ProfileFormRadioGroupProps) => {
  const { t } = useTranslation();
  const currentValue = values[name]?.toString() ?? "";

  return (
    <InputGroup title={t(`forms.labels.${name}.title`)}>
      <p className={cn("flex", typography.formInputSubtitle.className)}>
        {t(`forms.labels.${name}.description`)}
        {tooltip && (
          <Tooltip id={`tooltip-${name}`} message={t(`forms.labels.${name}.tooltip`)} />
        )}
      </p>
      <div>
        <RadioGroup
          name={name}
          options={options.map((option, index) => ({
            label: t(`forms.labels.${name}.option${index}`),
            value: option,
          }))}
          value={currentValue}
          onChange={handleChange}
        />
      </div>
    </InputGroup>
  );
};

interface SubmitButtonProps {
  block?: boolean;
  fp: FormikProps<FormValues>;
  ready: boolean;
  children: string;
}

export const SubmitButton = ({
  block = false,
  fp,
  ready,
  children,
}: SubmitButtonProps) => {
  return fp.isSubmitting ? (
    <Spinner size={24} />
  ) : (
    <Button block={block} disabled={!ready || !fp.isValid} type="submit">
      {children}
    </Button>
  );
};
